import useThemeColors from '@/hooks/useThemeColors';
import clsx from 'clsx';
import React from 'react';
import InputError from './InputError';

interface SelectInputProps {
    label?: string;
    id?: string;
    name: string;
    value: string | number;
    onChange: (value: string) => void;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    error?: string | string[] | null;
    helpText?: React.ReactNode;
    children: React.ReactNode;
}

const SelectInput: React.FC<SelectInputProps> = ({
    label,
    id,
    name,
    value,
    onChange,
    required = false,
    disabled = false,
    className = '',
    error,
    helpText,
    children,
}) => {
    const colors = useThemeColors();

    return (
        <div className={className}>
            {label && (
                <label
                    htmlFor={id || name}
                    className="mb-1 block text-sm font-medium text-gray-500 dark:text-neutral-400"
                >
                    {label}
                    {required && <span className="ml-1 text-red-500">*</span>}
                </label>
            )}

            <select
                id={id || name}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className={clsx(
                    'w-full rounded-lg border bg-white/10 px-3 py-2 text-sm text-gray-900 [box-shadow:0_3px_10px_rgba(0,0,0,0.22),0_1px_4px_rgba(0,0,0,0.15)] backdrop-blur-sm transition-colors focus:ring-2 focus:outline-none dark:bg-white/10 dark:text-white',
                    error
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                        : `border-white/50 dark:border-white/10 ${colors.inputFocus}`,
                    disabled && 'cursor-not-allowed opacity-60'
                )}
            >
                {children}
            </select>

            {helpText && !error && (
                <p className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                    {helpText}
                </p>
            )}

            <InputError error={error} />
        </div>
    );
};

SelectInput.displayName = 'SelectInput';

export default SelectInput;
